import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { requestOtp, verifyOtp } from "@/lib/identity.functions";
import type { PopupConfig } from "@/lib/identity/popupConfig";

const RESEND_COOLDOWN_SECONDS = 30;
const OTP_VALIDITY_SECONDS = 10 * 60;

function formatMMSS(total: number) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export type IssuedCoupon = {
  code: string;
  discountPercent: number;
  prepaidOnly: boolean;
  expiresAt: string;
} | null;

type Props = {
  popup: PopupConfig;
  anonymousId: string;
  onSuccess: (payload: { coupon: IssuedCoupon }) => void;
  onClose: () => void;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function OtpCaptureForm({ popup, anonymousId, onSuccess, onClose }: Props) {
  const [step, setStep] = useState<"details" | "otp">("details");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [validity, setValidity] = useState(0);
  const validityTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const ctaBg = "bg-[color:var(--brand,#1E5EFF)] hover:opacity-90 text-white";
  const expired = step === "otp" && validity === 0;

  function startCooldown(seconds: number = RESEND_COOLDOWN_SECONDS) {
    setCooldown(seconds);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          if (cooldownTimer.current) clearInterval(cooldownTimer.current);
          cooldownTimer.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  function startValidity(seconds: number = OTP_VALIDITY_SECONDS) {
    setValidity(seconds);
    if (validityTimer.current) clearInterval(validityTimer.current);
    validityTimer.current = setInterval(() => {
      setValidity((s) => {
        if (s <= 1) {
          if (validityTimer.current) clearInterval(validityTimer.current);
          validityTimer.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  useEffect(
    () => () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
      if (validityTimer.current) clearInterval(validityTimer.current);
    },
    [],
  );

  async function sendOtp(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!consent) {
      setErr("Please tick the consent box to continue.");
      return;
    }
    if (phone.length < 10) {
      setErr("Enter a valid 10-digit mobile number.");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setErr("Enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await requestOtp({ data: { anonymousId, phone, email: email.trim(), brand: popup.brand } });
      setStep("otp");
      setInfo(`Code sent to ${email.trim()}.`);
      startCooldown();
      startValidity();
    } catch (e: any) {
      setErr(e?.message ?? "Could not send code");
    } finally {
      setLoading(false);
    }
  }

  async function resendOtp() {
    if (cooldown > 0 || resending) return;
    setErr(null);
    setInfo(null);
    setResending(true);
    try {
      await requestOtp({ data: { anonymousId, phone, email: email.trim(), brand: popup.brand } });
      setInfo(`New code sent to ${email.trim()}.`);
      startCooldown();
      startValidity();
    } catch (e: any) {
      setErr(e?.message ?? "Could not resend code");
    } finally {
      setResending(false);
    }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (validity === 0) {
      setInfo(null);
      setErr("This code has expired. Tap “Resend code” to get a new one.");
      return;
    }
    setLoading(true);
    try {
      const result = await verifyOtp({
        data: {
          anonymousId,
          phone,
          email: email.trim(),
          code,
          consent: true,
          source: {
            popup_id: popup.id,
            offer: popup.offer,
            route: typeof window !== "undefined" ? window.location.pathname : undefined,
            brand: popup.brand,
          },
        },
      });
      onSuccess({ coupon: (result as { coupon?: IssuedCoupon }).coupon ?? null });
    } catch (e: any) {
      setErr(e?.message ?? "Could not verify");
    } finally {
      setLoading(false);
    }
  }

  if (step === "details") {
    return (
      <form onSubmit={sendOtp} className="space-y-4">
        <div>
          <label htmlFor="ip-phone" className="block text-sm font-medium text-slate-700">
            Mobile number
          </label>
          <div className="mt-1 flex items-stretch rounded-lg border border-slate-300 bg-white focus-within:border-slate-500">
            <span className="flex items-center px-3 text-sm text-slate-500">+91</span>
            <input
              id="ip-phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              required
              minLength={10}
              maxLength={13}
              pattern="[0-9]{10,13}"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="9876543210"
              className="w-full rounded-r-lg bg-transparent px-2 py-2.5 text-base outline-none"
            />
          </div>
        </div>

        <div>
          <label htmlFor="ip-email" className="block text-sm font-medium text-slate-700">
            Email
          </label>
          <input
            id="ip-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base outline-none focus:border-slate-500"
          />
          <p className="mt-1 text-xs text-slate-500">
            We'll email a 6-digit code to verify, your offer unlocks once you confirm.
          </p>
        </div>

        <label className="flex items-start gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            required
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-400"
          />
          <span>
            I accept the{" "}
            <a href="/policies/terms-of-service" className="underline" target="_blank" rel="noreferrer">
              Terms &amp; Conditions
            </a>{" "}
            and{" "}
            <a href="/policies/privacy-policy" className="underline" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            , and agree to receive my offer and occasional updates from TruWe by email, WhatsApp or
            SMS. I can opt out anytime.
          </span>
        </label>

        {err ? <p className="text-sm text-red-600">{err}</p> : null}

        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 underline-offset-4 hover:underline"
          >
            No thanks
          </button>
          <button
            type="submit"
            disabled={loading || !consent || phone.length < 10 || !EMAIL_RE.test(email.trim())}
            className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition disabled:opacity-50 ${ctaBg}`}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {popup.cta}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={confirm} className="space-y-4">
      <div>
        <label htmlFor="ip-otp" className="block text-sm font-medium text-slate-700">
          Enter 6-digit code
        </label>
        <input
          id="ip-otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          pattern="\d{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-center text-2xl tracking-[0.5em] outline-none focus:border-slate-500"
          aria-describedby="ip-otp-help"
        />
        <p id="ip-otp-help" className="mt-1 flex items-center justify-between text-xs">
          <span className="text-slate-500">Sent to {email}.</span>
          {expired ? (
            <span className="font-medium text-red-600">Code expired</span>
          ) : (
            <span className="font-medium text-slate-700">
              Expires in {formatMMSS(validity)}
            </span>
          )}
        </p>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">Didn't get it? Check spam.</span>
        <button
          type="button"
          onClick={resendOtp}
          disabled={cooldown > 0 || resending || loading}
          className="inline-flex items-center gap-1 font-medium text-[color:var(--brand,#1E5EFF)] disabled:cursor-not-allowed disabled:text-slate-400"
        >
          {resending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>

      {expired ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          Your 6-digit code has expired. Tap <span className="font-semibold">Resend code</span> above to get a new one.
        </div>
      ) : null}

      {info && !err && !expired ? <p className="text-sm text-emerald-600">{info}</p> : null}
      {err ? <p className="text-sm text-red-600">{err}</p> : null}

      <div className="flex items-center justify-between gap-3 pt-1">
        <button
          type="button"
          onClick={() => setStep("details")}
          className="text-sm text-slate-500 underline-offset-4 hover:underline"
        >
          Edit details
        </button>
        <button
          type="submit"
          disabled={loading || code.length !== 6 || expired}
          className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition disabled:opacity-50 ${ctaBg}`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Verify & claim
        </button>
      </div>
    </form>
  );
}
